import { useCssVar, toReactive, useCurrentElement } from '@vueuse/core';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * This function allows us to merge tailwind classes in an overrideable way:
 * e.g., a component can have `class="font-bold"` and that can be overriden by
 * passing `class="font-normal"` on consumption.
 *
 * See BaseHeading.vue for an example usage.
 *
 * This should likely only be used on generic components, not everywhere: while
 * this supports all the functionality of `clsx`, we don't usually need that in
 * Vue as it's got it all built in, except for the tailwind merging.
 */
export const cn = (...inputs: ClassValue[]) => twMerge(clsx(inputs));

/**
 * Helper function for using CSS variables defined in our tailwind config.
 *
 * Returns multiple reactive objects so that if someone tries to destructure
 * the return value, it won't break reactivity
 */
export function useColors() {
  // The target isn't configurable and if you're trying to configure it, you're
  // probably trying to do something that you shouldn't be
  const currentEl = useCurrentElement();

  const neutral50 = useCssVar('--color-neutral-50', currentEl);
  const neutral300 = useCssVar('--color-neutral-300', currentEl);
  const neutral500 = useCssVar('--color-neutral-500', currentEl);
  const neutral600 = useCssVar('--color-neutral-600', currentEl);
  const neutral800 = useCssVar('--color-neutral-800', currentEl);

  const blue500 = useCssVar('--color-blue-500', currentEl);
  const blue700 = useCssVar('--color-blue-700', currentEl);

  const mint300 = useCssVar('--color-mint-300', currentEl);
  const mint400 = useCssVar('--color-mint-400', currentEl);
  const mint500 = useCssVar('--color-mint-500', currentEl);

  const colors = toReactive(
    computed(() => {
      if (
        !neutral50.value ||
        !neutral300.value ||
        !neutral500.value ||
        !neutral600.value ||
        !neutral800.value ||
        !blue500.value ||
        !blue700.value ||
        !mint300.value ||
        !mint400.value ||
        !mint500.value
      ) {
        throw new Error('expected colour not found');
      }

      return {
        neutral50: neutral50.value,
        neutral300: neutral300.value,
        neutral500: neutral500.value,
        neutral600: neutral600.value,
        neutral800: neutral800.value,
        blue500: blue500.value,
        blue700: blue700.value,
        mint300: mint300.value,
        mint400: mint400.value,
        mint500: mint500.value,
      };
    }),
  );

  const accentPrimary = useCssVar('--accent-color-primary', currentEl);

  const accent = toReactive(
    computed(() => {
      if (!accentPrimary.value) {
        throw new Error('expected colour not found');
      }

      return {
        primary: accentPrimary.value,
      };
    }),
  );

  const borderNeutral = useCssVar('--border-color-neutral', currentEl);
  const borderNeutralMuted = useCssVar('--border-color-neutral-muted', currentEl);

  const border = toReactive(
    computed(() => {
      if (!borderNeutral.value || !borderNeutralMuted.value) {
        throw new Error('expected colour not found');
      }

      return {
        neutral: borderNeutral.value,
        neutralMuted: borderNeutralMuted.value,
      };
    }),
  );

  const bgDefault = useCssVar('--background-color-default', currentEl);
  const bgNeutral = useCssVar('--background-color-neutral', currentEl);

  const bg = toReactive(
    computed(() => {
      if (!bgDefault.value || !bgNeutral.value) {
        throw new Error('expected colour not found');
      }

      return {
        default: bgDefault.value,
        neutral: bgNeutral.value,
      };
    }),
  );

  // Using this is an anti-pattern and should be avoided wherever possible in
  // favour of the colours above. This is used as an escape hatch where we want
  // to e.g. pass the user's colour preference to an external service
  const isDarkDangerous = computed(() => {
    if (!(currentEl.value instanceof HTMLElement)) {
      return false;
    }

    return !!currentEl.value.closest('.tw-dark');
  });

  return { colors, accent, border, bg, isDarkDangerous };
}
